import request from "@/requests/request";

export const getShopifyStoreDetails = ({
  signal,
}: IGenericHookOptions): Promise<IGetShopifyStoreDetailsRS> =>
  request({
    url: "/v1/shopify-integration/store-details",
    signal,
  }).then(res => res.data);

export const postPrivacyPolicyUrl = async ({
  privacyPolicyUrl,
  version,
}: IPostPrivacyPolicyUrlRQ): Promise<IPostPrivacyPolicyUrlRS> =>
  request({
    url: "/v1/consent-form-setup/privacy-policy-url",
    method: "POST",
    body: { privacyPolicyUrl, version },
  }).then(res => res.data);

export const postPrivacyEmail = async ({
  privacyEmail,
  version,
}: IPostPrivacyEmailRQ): Promise<IPostPrivacyEmailRS> =>
  request({
    url: "/v1/consent-form-setup/privacy-email",
    method: "POST",
    body: { privacyEmail, version },
  }).then(res => res.data);

export const getConsentFormFields = async ({
  signal,
}: IGenericHookOptions): Promise<IGetConsentFieldsRS> =>
  request({
    url: "/v1/consent-form-setup",
    signal,
  }).then(res => res.data);

export const postKlaviyoOptIn = async ({
  signal,
}: IGenericHookOptions): Promise<IGetKlaviyoOptInRS> =>
  request({
    url: "/v1/klaviyo-integration/list",
    method: "POST",
    signal,
  }).then(res => res.data);

export const getCheckKlaviyoOptIn = async ({
  signal,
}: IGenericHookOptions): Promise<ICheckKlaviyoOptInRS> =>
  request({
    url: "/v1/klaviyo-integration/check-single-optin-list",
    signal,
  }).then(res => res.data);

export const postUpdateOnboardingProgress = async ({
  screen,
  flow,
}: IPostUpdateOnboardingProgressRQ): Promise<void> =>
  request({
    url: `/v1/customer/onboarding-progress/${flow.toLowerCase()}`,
    method: "POST",
    body: {
      onboardingProgress: screen,
    },
  }).then(res => res.data);

export const postVerifyKlaviyoPrivateKey = async ({
  privateKey,
  signal,
}: {
  privateKey: string;
  signal: IGenericHookOptions["signal"];
}): Promise<IVerifyKlaviyoPrivateKeyRS> =>
  await request({
    url: "/v1/klaviyo-integration/organization-name",
    method: "POST",
    body: {
      privateApiKey: privateKey,
    },
    signal,
  }).then(res => res?.data);

export const getKlaviyoPrivateKey = async ({
  signal,
}: IGenericHookOptions): Promise<IGetKlaviyoPrivateKeyRS> =>
  await request({
    url: "/v1/klaviyo-integration/private-key-details",
    signal,
  }).then(res => res?.data);

export const postKlaviyoPrivateKey = async (privateKey: string) =>
  await request({
    url: "/v1/klaviyo-integration/private-key",
    method: "POST",
    body: {
      privateApiKey: privateKey,
    },
  }).then(res => res?.data);

export const getWidgetCode = async ({
  signal,
}: IGenericHookOptions): Promise<IGetShopifyWidgetCodeRS> =>
  await request({
    url: "/v1/code-snippets/widgets/shopify-confirmation",
    signal,
  }).then(res => res?.data);

export const getShopifyIntegration = async ({
  signal,
}: IGenericHookOptions): Promise<IGetShopifyIntegrationRS> =>
  await request({
    url: "/v1/shopify-integration",
    signal,
  }).then(res => res.data);

export const getComplianceReports = async ({
  params,
  signal,
}: {
  params: ITableParams;
  signal: IGenericHookOptions["signal"];
}): Promise<IGetComplianceReportsRS> =>
  await request({
    url: "/v1/compliance-reports",
    params,
    signal,
  }).then(res => res.data);

export const getEmailDashboard = async ({
  payload,
  signal,
}: {
  payload: IGetEmailDashboardRQ;
  signal: IGenericHookOptions["signal"];
}): Promise<IGetEmailDashboardRS> =>
  await request({
    url: "/v3/dashboard/overview",
    params: payload,
    signal,
  }).then(res => res.data);

export const getUsageDashboard = async ({
  payload,
  signal,
}: {
  payload: IGetDashboardRQ;
  signal: IGenericHookOptions["signal"];
}): Promise<IGetUsageDashboardRS> =>
  await request({
    url: "/v1/dashboard/usage",
    params: payload,
    signal,
  }).then(res => res.data);

export const getComplianceReport = async ({
  reportId,
  signal,
}: {
  reportId?: string;
  signal: IGenericHookOptions["signal"];
}): Promise<IComplianceReport> =>
  await request({
    url: `/v1/compliance-reports/${reportId}`,
    signal,
  }).then(res => res.data);

export const setComplianceReport = async (
  reportId: string,
): Promise<IComplianceReport> =>
  await request({
    url: `/v1/compliance-reports/${reportId}/complete`,
    method: "POST",
  }).then(res => res.data);

export const getContacts = async ({
  params,
  signal,
}: {
  params: ITableParams;
  signal: IGenericHookOptions["signal"];
}): Promise<IGetContactsRS> =>
  await request({
    url: "/v1/contacts",
    params,
    signal,
  }).then(res => res.data);

export const getContactDetails = async (
  contactId?: string,
): Promise<IGetContactDetailsRS> =>
  await request({
    url: `/v1/contacts/${contactId}/details`,
  }).then(res => res.data);

export const getContactHistory = async (
  params: ITableParams,
): Promise<IGetContactHistoryRS> =>
  await request({
    url: `/v1/contacts/${params.contactId}/history`,
    params: params,
  }).then(res => res.data);

export const getContactHistoryDetails = async (
  historyId?: string,
): Promise<IGetContactDetailsRS> =>
  await request({
    url: `/v1/contacts/history-details/${historyId}`,
  }).then(res => res.data);

export const getSmsHealthChecks = async (): Promise<IGetHealthChecksRS> =>
  await request({
    url: "/v1/sms/health-checks",
    method: "GET",
  }).then(res => res.data);

export const getShopifyHealthCheckHistory = async ({
  params,
  signal,
}: {
  params: ITableParams;
  signal: IGenericHookOptions["signal"];
}): Promise<IGetShopifyHealthHistoryRS> =>
  await request({
    url: "/v1/sms/health-checks/history/shopify",
    params,
    signal,
  }).then(res => res.data);

export const getKlaviyoHealthCheckHistory = async ({
  params,
  signal,
}: {
  params: ITableParams;
  signal: IGenericHookOptions["signal"];
}): Promise<IGetKlaviyoHealthHistoryRS> =>
  await request({
    url: "/v1/sms/health-checks/history/klaviyo",
    params,
    signal,
  }).then(res => res.data);

export const getSmsOnboardingPolicyContent = async ({
  signal,
  token,
}: {
  signal: IGenericHookOptions["signal"];
  token: string;
}): Promise<ISmsOnboardingPolicyContent> =>
  await request({
    url: `/v1/sms/onboarding-policy-content/${token}`,
    signal,
  }).then(res => res.data);

export const getDatashipsKlaviyoLists = async ({
  signal,
  isKlaviyoV2EndpointEnabled,
}: {
  signal: IGenericHookOptions["signal"];
  isKlaviyoV2EndpointEnabled: boolean;
}): Promise<IGetDatashipsKlaviyoLists | IDatashipsKlaviyoList[]> =>
  await request({
    url: `/${isKlaviyoV2EndpointEnabled ? "v2" : "v1"}/klaviyo-integration/lists/dataships`,
    signal,
  }).then(res => res.data);

export const getAllKlaviyoLists = async (): Promise<IGetAllKlaviyoLists> =>
  await request({
    url: `/v1/klaviyo-integration/lists/all`,
  }).then(res => res.data);

export const updateKlaviyoList = async ({
  type,
  id,
}: IKlaviyoList): Promise<void> =>
  await request({
    url: `/v1/klaviyo-integration/list/${type}`,
    method: "POST",
    body: {
      listId: id,
    },
  }).then(res => res.data);

export const updateKlaviyoListV2 = async ({
  type,
  id,
  channel,
}: IKlaviyoList): Promise<void> =>
  await request({
    url: `/v2/klaviyo-integration/list`,
    method: "POST",
    body: {
      listId: id,
      listType: type,
      channel,
    },
  }).then(res => res.data);

export const getCompanyProfile = async (): Promise<IGetCompanyProfile> =>
  await request({
    url: "/v1/customer/profile",
  }).then(res => res.data);

export const postCompanyProfile = async (
  companyProfile: ICompanyProfile,
): Promise<void> =>
  await request({
    url: "/v1/customer/profile",
    method: "POST",
    body: companyProfile,
  }).then(res => res.data);

export const getPoweredByDatashipsPreference =
  async (): Promise<IPoweredByDatashipsPreference> =>
    await request({
      url: "/v1/customer/display-widget-footer",
      method: "GET",
    }).then(res => res.data);

export const postTogglePoweredByDatashipsPreference = async (
  newValue: boolean,
): Promise<void> =>
  await request({
    url: "/v1/customer/display-widget-footer",
    method: "POST",
    body: {
      shouldDisplayFooter: newValue,
    },
  }).then(res => res.data);
